import type { SpecificBook } from "@book-club/shared";
import { isBookArchived, isScheduledBook, isSpecificBook } from "@book-club/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import booksService from "@/features/books/services/booksService";
import DURATIONS from "@/shared/constants/durations";
import { booksKeys } from "./booksQueryKeys";

/**
 * Fetches all unscheduled specificBook types from Firestore
 * Filters out scheduled books (those with year/month fields) and non-specificBook types
 * Uses 1 day staleTime for caching
 * Shows toast notification on error
 * @returns Query result with unscheduled specificBook types only
 */
export function useUnscheduledBooks() {
    const query = useQuery<SpecificBook[], Error>({
        gcTime: DURATIONS.ONE_DAY,
        queryFn: async () => {
            const allBooks = await booksService.getAllBooks();
            // Filter to only unscheduled specificBook types
            return allBooks.filter((book): book is SpecificBook => {
                if (!isSpecificBook(book)) {
                    return false;
                }
                if (isBookArchived(book)) {
                    return false;
                }
                return !isScheduledBook(book);
            });
        },
        queryKey: booksKeys.unscheduled(),
        staleTime: DURATIONS.ONE_DAY,
    });

    // Show toast notification on error
    useEffect(() => {
        if (query.isError && query.error) {
            const errorMessage = query.error instanceof Error ? query.error.message : "Failed to load books";
            toast.error(errorMessage);
        }
    }, [query.isError, query.error]);

    return query;
}
